/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Portlet,
  PortletContent,
  PortletHeader,
  PortletLabel,
  PortletToolbar,
} from 'components';
import { useIntl } from 'react-intl';
import { Download, ShareButton } from 'components/Download';
// import { parse } from 'date-fns';

const TITLES = {
  CORRIENTE_RMS: 'Corriente RMS',
  VOLTAJE_RMS: 'Voltaje RMS',
};

export default function LineChart({ title, dataChart }) {
  const [data, setData] = useState([]);
  const [selectLine, setSelectLine] = useState(1);
  const colors = ['#333333', '#FF4A4A', '#00AFEF', '#9e9e9e'];
  const intl = useIntl();
  const chartRef = useRef(null);

  // const parsed = (date) => parse(date, 'yyyyMMddHHmm', new Date());
  const getMaxPoint = (key) => {
    const values = data
      .map((d) => ({
        time: d.datetime.getTime(),
        value: d[key],
      }))
      .filter((v) => v.value !== null && v.value !== undefined);

    const max = Math.max(...values.map((v) => v.value));
    return values.find((v) => v.value === max);
  };

  const getMinPoint = (key) => {
    const values = data
      .map((d) => ({
        time: d.datetime.getTime(),
        value: d[key],
      }))
      .filter((v) => v.value !== null && v.value !== undefined);

    const min = Math.min(...values.map((v) => v.value));
    return values.find((v) => v.value === min);
  };

  const minL1 = getMinPoint('l1');
  const minL2 = getMinPoint('l2');
  const minL3 = getMinPoint('l3');
  const minN = title === TITLES.CORRIENTE_RMS ? getMinPoint('n') : null;

  const maxL1 = getMaxPoint('l1');
  const maxL2 = getMaxPoint('l2');
  const maxL3 = getMaxPoint('l3');
  const maxN = title === TITLES.CORRIENTE_RMS ? getMaxPoint('n') : null;

  const scatterSeries = [
    {
      type: 'scatter',
      name: 'Minímo',
      data: [],
      color: '#fff',
      marker: {
        enabled: true,
        radius: 6,
        symbol: 'triangle-down',
        fillColor: '#fff',
        lineColor: '#000',
        lineWidth: 1,
      },
      tooltip: {
        enabled: true,
      },
    },
    {
      type: 'scatter',
      name: 'Máximo',
      data: [],
      color: '#fff',
      marker: {
        enabled: true,
        radius: 6,
        symbol: 'triangle',
        fillColor: '#fff',
        lineColor: '#000',
        lineWidth: 1,
      },
      tooltip: {
        enabled: true,
      },
    },
    {
      type: 'scatter',
      name: 'Máximo Fase 1',
      data: maxL1 ? [[maxL1.time, maxL1.value]] : [],
      color: colors[0],
      marker: {
        enabled: true,
        radius: 6,
        symbol: 'triangle',
        fillColor: colors[0],
        lineColor: '#000',
        lineWidth: 1,
      },
      tooltip: {
        enabled: true,
      },
      enableMouseTracking: true,
      showInLegend: false,
    },
    {
      type: 'scatter',
      name: 'Minímo Fase 1',
      data: minL1 ? [[minL1.time, minL1.value]] : [],
      color: colors[0],
      marker: {
        enabled: true,
        radius: 6,
        symbol: 'triangle-down',
        fillColor: colors[0],
        lineColor: '#000',
        lineWidth: 1,
      },
      enableMouseTracking: true,
      tooltip: {
        enabled: true,
      },
      showInLegend: false,
    },
    {
      type: 'scatter',
      name: 'Máximo Fase 2',
      data: maxL2 ? [[maxL2.time, maxL2.value]] : [],
      color: colors[1],
      marker: {
        enabled: true,
        radius: 6,
        symbol: 'triangle',
        fillColor: colors[1],
        lineColor: '#000',
        lineWidth: 1,
      },
      enableMouseTracking: true,
      tooltip: {
        enabled: true,
      },
      showInLegend: false,
    },
    {
      type: 'scatter',
      name: 'Minímo Fase 2',
      data: minL2 ? [[minL2.time, minL2.value]] : [],
      color: colors[1],
      marker: {
        enabled: true,
        radius: 6,
        symbol: 'triangle-down',
        fillColor: colors[1],
        lineColor: '#000',
        lineWidth: 1,
      },
      enableMouseTracking: true,
      tooltip: {
        enabled: true,
      },
      showInLegend: false,
    },
    {
      type: 'scatter',
      name: 'Máximo Fase 3',
      data: maxL3 ? [[maxL3.time, maxL3.value]] : [],
      color: colors[2],
      marker: {
        enabled: true,
        radius: 6,
        symbol: 'triangle',
        fillColor: colors[2],
        lineColor: '#000',
        lineWidth: 1,
      },
      enableMouseTracking: true,
      tooltip: {
        enabled: true,
      },
      showInLegend: false,
    },
    {
      type: 'scatter',
      name: 'Minímo Fase 3',
      data: minL3 ? [[minL3.time, minL3.value]] : [],
      color: colors[2],
      marker: {
        enabled: true,
        radius: 6,
        symbol: 'triangle-down',
        fillColor: colors[2],
        lineColor: '#000',
        lineWidth: 1,
      },
      enableMouseTracking: true,
      tooltip: {
        enabled: true,
      },
      showInLegend: false,
    },
    ...(title === TITLES.CORRIENTE_RMS && maxN
      ? [
          {
            type: 'scatter',
            name: 'Máximo Neutro',
            data: [[maxN.time, maxN.value]],
            color: colors[3],
            marker: {
              enabled: true,
              radius: 6,
              symbol: 'triangle',
              fillColor: colors[3],
              lineColor: '#000',
              lineWidth: 1,
            },
            yAxis: 1,
            enableMouseTracking: true,
            tooltip: {
              enabled: true,
            },
            showInLegend: false,
          },
          {
            type: 'scatter',
            name: 'Minímo Neutro',
            data: [[minN.time, minN.value]],
            color: colors[3],
            marker: {
              enabled: true,
              radius: 6,
              symbol: 'triangle-down',
              fillColor: colors[3],
              lineColor: '#000',
              lineWidth: 1,
            },
            yAxis: 1,
            enableMouseTracking: true,
            tooltip: {
              enabled: true,
            },
            showInLegend: false,
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (!dataChart.length) {
      setData([]);
      return;
    }

    let key;
    switch (title) {
      case TITLES.CORRIENTE_RMS:
        key = 'corriente_rms';
        break;
      case TITLES.VOLTAJE_RMS:
        key = 'voltaje_rms';
        break;
      default:
        return;
    }

    const formatted = dataChart.map((element) => {
      return {
        l1: element.mediciones?.[key].L1[selectLine],
        l2: element.mediciones?.[key].L2[selectLine],
        l3: element.mediciones?.[key].L3[selectLine],
        datetime: new Date(element?.timestamp),
        ...(title === TITLES.CORRIENTE_RMS && {
          n: element.mediciones?.corriente_rms.N[selectLine],
        }),
      };
    });

    formatted.sort((a, b) => a.datetime - b.datetime);
    setData(formatted);
  }, [dataChart, title, selectLine]);

  const series = [
    {
      name: intl.formatMessage({ id: 'app.deepview.current' }) + ' 1',
      data: data.map((d) => [d.datetime.getTime(), d.l1]),
      color: colors[0],
    },
    {
      name: intl.formatMessage({ id: 'app.deepview.current' }) + ' 2',
      data: data.map((d) => [d.datetime.getTime(), d.l2]),
      color: colors[1],
    },
    {
      name: intl.formatMessage({ id: 'app.deepview.current' }) + ' 3',
      data: data.map((d) => [d.datetime.getTime(), d.l3]),
      color: colors[2],
    },
    ...(title === TITLES.CORRIENTE_RMS
      ? [
          {
            name: 'Neutro',
            data: data.map((d) => [d.datetime.getTime(), d.n]),
            yAxis: 1,
            color: colors[3],
          },
        ]
      : []),
  ];

  const options = {
    chart: {
      type: 'line',
      zoomType: 'x',
      height: 450,
    },
    title: { text: '' },
    global: {
      useUTC: true,
    },
    time: {
      useUTC: true,
    },
    xAxis: {
      type: 'datetime',
      title: {
        text: 'Tiempo',
      },
      labels: {
        format: '{value:%H:%M}',
      },
    },
    yAxis: [
      {
        title: { text: title },
      },
      ...(title === TITLES.CORRIENTE_RMS
        ? [
            {
              title: { text: 'Neutral' },
              opposite: true,
            },
          ]
        : []),
    ],
    tooltip: {
      shared: true,
      xDateFormat: '%A, %b %e, %H:%M:%S UTC',
      distance: 20,
      split: true,
      stickOnContact: true,
      formatter: function () {
        const date = new Date(this.x);
        const locale = intl.formatMessage({ id: 'app.locale' });
        const options = {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: 'numeric',
          minute: 'numeric',
          timeZone: 'UTC',
        };

        let formattedDate = date
          .toLocaleString(locale, options)
          .replace(',', '');
        let s = `<b>${formattedDate}</b><br/>`;

        this?.points?.forEach((point) => {
          s += `<span style="color:${point.series.color}">\u25CF</span> ${point.series.name}: <b>${Highcharts.numberFormat(point.y, 2)}</b><br/>`;
        });

        // Detectar si hay un punto individual (scatter no compartido)
        if (this.point && !this.points) {
          s += `<span style="color:${this.point.series.color}">\u25CF</span> ${this.point.series.name}: <b>${Highcharts.numberFormat(this.point.y, 2)}</b><br/>`;
        }

        return s;
      },
    },
    plotOptions: {
      series: {
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 4,
            },
          },
        },
      },
    },
    legend: { enabled: true },
    series: [...series, ...scatterSeries],
  };

  useEffect(() => {
    Highcharts.setOptions({
      global: {
        useUTC: true,
      },
      time: {
        useUTC: true, // Configuración adicional para el módulo de tiempo de Highcharts
      },
    });
  }, []);

  return (
    <Box width="100%" ref={chartRef}>
      <Portlet>
        <PortletHeader noDivider={true}>
          <PortletLabel title={title ? title : 'Title'} />
          <PortletToolbar>
            <FormControl fullWidth sx={{ mr: 2 }}>
              <InputLabel id="line-select-label">Mostrar por:</InputLabel>
              <Select
                labelId="line-select-label"
                value={selectLine}
                onChange={(e) => setSelectLine(e.target.value)}
                label="Mostrar por:"
                variant="outlined"
              >
                <MenuItem value={0}>Máximo</MenuItem>
                <MenuItem value={1}>Promedio</MenuItem>
                <MenuItem value={2}>Mínimo</MenuItem>
              </Select>
            </FormControl>
            <Box display="flex" gap={1}>
              <Download name={title} itemRef={chartRef} />
              <ShareButton itemRef={chartRef} name={title} />
            </Box>
          </PortletToolbar>
        </PortletHeader>
        <PortletContent>
          <HighchartsReact highcharts={Highcharts} options={options} />
        </PortletContent>
      </Portlet>
      {/* Tabla */}
      <Box width="100%" mt={3}>
        <Box
          display="flex"
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
          gap={3}
        >
          <Chip
            label={title}
            sx={{
              fontWeight: 700,
              backgroundColor: '#730065',
              fontSize: '16px',
              color: 'white',
            }}
          />
          <Table sx={{ maxWidth: 650, boxShadow: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>{''}</TableCell>
                <TableCell align="center">Minímo</TableCell>
                <TableCell align="center">Máximo</TableCell>
                <TableCell align="center">Promedio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                'l1',
                'l2',
                'l3',
                ...(title === TITLES.CORRIENTE_RMS ? ['n'] : []),
              ].map((key, index) => (
                <TableRow key={key}>
                  <TableCell align="center">
                    {key === 'n'
                      ? 'Neutro'
                      : `${intl.formatMessage({ id: 'app.deepview.current' }) + ' '}${index + 1}`}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      bgcolor: '#8CBA42',
                      fontWeight: 'bold',
                      color: 'white',
                    }}
                  >
                    {data.length
                      ? Math.min(...data.map((d) => d[key])).toFixed(4)
                      : 0}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      bgcolor: '#D64539',
                      fontWeight: 'bold',
                      color: 'white',
                    }}
                  >
                    {data.length
                      ? Math.max(...data.map((d) => d[key])).toFixed(4)
                      : 0}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      bgcolor: '#EAAB36',
                      fontWeight: 'bold',
                      color: 'white',
                    }}
                  >
                    {data.length
                      ? (
                          data.map((d) => d[key]).reduce((a, b) => a + b, 0) /
                          data.length
                        ).toFixed(4)
                      : 0}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
}
